// Builds the lab's "Performance Test Report" Excel workbook for a steady-state
// test by filling the report template's S (1), S (2) and TC sheets from the
// readings a lab agent streamed.
import ExcelJS from 'exceljs'
import { saveAs } from 'file-saver'
import { cleanText, namedChannels, readingDecimals } from '@/components/labTelemetry/telemetryDataset'
import { groupChannels } from '@/components/labTelemetry/channelPresets'

const TEMPLATE_URL = `${process.env.BASE_URL}templates/performance-report-template.xlsx`
const XLSX_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
export const REPORT_SHEETS = ['S (1)', 'S (2)', 'TC']
const SAMPLE_COLUMNS = ['J', 'L', 'N', 'P', 'R', 'T', 'V']
const AVERAGE_COLUMN = 'X'
const NO_VALUE = '--'

// The MDB stores SI values; some report rows are printed in other units
const PA_TO_INH2O = 1 / 249.0889
const M3H_TO_CFM = 0.5885778
const M3KG_TO_FT3LB = 16.018463
const KJKG_TO_KCALKG = 1 / 4.1868
const MPA_TO_PSI = 145.0377
const KGS_TO_LBHR = 7936.641
const FRACTION_TO_PERCENT = 100

// Report rows -> MDB channel (the `Temp` table's Name), decimals, and an
// optional unit conversion factor. `channel: null` rows have no MDB source.
const S1_ROWS = [
  { row: 29, channel: 'UUT Capacity', decimals: 1 },
  { row: 30, channel: 'Calculated Fan Watts', decimals: 1 },
  { row: 31, channel: 'Calculated Fan BTU\'s', decimals: 1 },
  { row: 32, channel: 'UUT Power', decimals: 1 },
  { row: 33, channel: 'OD UUT Power', decimals: 1 },
  { row: 34, channel: 'ID UUT Power', decimals: 1 },
  { row: 35, channel: 'COP', decimals: 3 },
  { row: 36, channel: 'EER2', decimals: 3 },
  { row: 37, channel: 'ID Airflow(CFM)', decimals: 2 },
  { row: 38, channel: 'ID Standard Airflow', decimals: 2, factor: M3H_TO_CFM },
  { row: 39, channel: 'ID Airflow Velocity', decimals: 2 },
  { row: 40, channel: 'Net Refrigeration Capacity', decimals: 1 },
  { row: 41, channel: 'Gross Refrigerant Capacity', decimals: 1 },
  { row: 42, channel: 'Heat balance', decimals: 1 },
  { row: 49, channel: 'Power ¢ñ #1', decimals: 1 },
  { row: 50, channel: 'Voltage ¢ñ #1', decimals: 1 },
  { row: 51, channel: 'Current ¢ñ #1', decimals: 2 },
  { row: 52, channel: 'Voltage Frequency #1', decimals: 1 },
  { row: 53, channel: 'Power Factor ¢ñ #1', decimals: 2, factor: FRACTION_TO_PERCENT },
  { row: 54, channel: 'Power ¢ñ #2', decimals: 1 },
  { row: 55, channel: 'Voltage ¢ñ #2', decimals: 1 },
  { row: 56, channel: 'Current ¢ñ #2', decimals: 2 },
  { row: 57, channel: 'Voltage Frequency #2', decimals: 1 },
  { row: 58, channel: 'Power Factor ¢ñ #2', decimals: 2, factor: FRACTION_TO_PERCENT }
]

const S2_ROWS = [
  { row: 11, channel: 'UUT Capacity', decimals: 1 },
  { row: 12, channel: 'ID Sensible Capacity', decimals: 1 },
  { row: 13, channel: 'ID Latent Capacity', decimals: 1 },
  { row: 14, channel: 'ID Sensible to Total Ratio', decimals: 2 },
  { row: 15, channel: 'Sensible Via Grids', decimals: 1 },
  { row: 16, channel: 'ID Duct Loss', decimals: 1 },
  { row: 17, channel: 'Return Duct Loss', decimals: 1 },
  { row: 18, channel: 'Duct Area SF', decimals: 3 },
  { row: 19, channel: 'Condensate Flow', decimals: 2 },
  { row: 20, channel: 'ID Airflow(CFM)', decimals: 2 },
  { row: 21, channel: 'ID Standard Airflow', decimals: 2, factor: M3H_TO_CFM },
  { row: 22, channel: 'ID Static Pressure', decimals: 3, factor: PA_TO_INH2O },
  { row: 23, channel: 'Nozzles Diff. Pressure', decimals: 3, factor: PA_TO_INH2O },
  { row: 24, channel: 'Nozzles  Pressure', decimals: 3, factor: PA_TO_INH2O },
  { row: 25, channel: 'Nozzle Area', decimals: 4 },
  { row: 26, channel: 'Specific Volume Dry', decimals: 4, factor: M3KG_TO_FT3LB },
  { row: 27, channel: 'Specific Volume Mix', decimals: 4, factor: M3KG_TO_FT3LB },
  { row: 28, channel: 'ID DB before Nozzles', decimals: 2 },
  { row: 29, channel: 'Barometric Pressure', decimals: 2 },
  { row: 30, channel: 'ID Entering DB', decimals: 2 },
  { row: 31, channel: 'ID Entering WB', decimals: 2 },
  { row: 32, channel: null },
  { row: 33, channel: null },
  { row: 34, channel: 'ID Leaving DB', decimals: 2 },
  { row: 35, channel: 'ID Leaving WB', decimals: 2 },
  { row: 36, channel: null },
  { row: 37, channel: null },
  { row: 38, channel: 'ID Inlet HM', decimals: 2 },
  { row: 39, channel: 'Indoor Dewpoint', decimals: 2 },
  { row: 40, channel: 'OD Entering DB #1', decimals: 2 },
  { row: 41, channel: 'OD Entering WB', decimals: 2 },
  { row: 42, channel: 'OD Entering DB #2', decimals: 2 },
  { row: 43, channel: null },
  { row: 44, channel: 'OD Entering HM', decimals: 2 },
  { row: 45, channel: 'Outdoor Dewpoint', decimals: 2 },
  { row: 46, channel: 'Conversion WB', decimals: 2 },
  { row: 47, channel: 'ID Entering Air Enthalpy', decimals: 3, factor: KJKG_TO_KCALKG },
  { row: 48, channel: 'ID Leaving Air Enthalpy', decimals: 3, factor: KJKG_TO_KCALKG },
  { row: 49, channel: 'Net Refrigeration Capacity', decimals: 1 },
  { row: 50, channel: 'Refrigerant Mass Flow Rate', decimals: 3, factor: KGS_TO_LBHR },
  { row: 51, channel: 'P5 Liq TXV / Flow Meter Inlet', decimals: 2, factor: MPA_TO_PSI },
  { row: 52, channel: 'Flow Meter Inlet Temperature', decimals: 2 },
  { row: 53, channel: 'Refrigerant Inlet Enthalpy', decimals: 3, factor: KJKG_TO_KCALKG },
  { row: 54, channel: 'SC P5 Liq. at TXV', decimals: 2 },
  { row: 55, channel: 'P6 Gas ID Coil / Flow Meter Outlet', decimals: 2, factor: MPA_TO_PSI },
  { row: 56, channel: 'Flow Meter Outlet Temperature', decimals: 2 },
  { row: 57, channel: 'Refrigerant Outlet Enthalpy', decimals: 3, factor: KJKG_TO_KCALKG },
  { row: 58, channel: 'SH P6 Gas ID Coil', decimals: 2 },
  { row: 59, channel: 'SH P1 Compressor Discharge', decimals: 2 },
  { row: 60, channel: 'SH P2 Gas OD Coil', decimals: 2 },
  { row: 61, channel: 'SC P4 Liq. Service Valve', decimals: 2 },
  { row: 62, channel: 'SH P7 Gas Service valve', decimals: 2 },
  { row: 63, channel: 'SH P8 Compressor Suction', decimals: 2 },
  ...[64, 65, 66, 67, 68, 69, 70, 71, 72, 73].map(row => ({ row, channel: null }))
]

// The TC sheet lists the thermocouples of each side in their MDB order
const TC_SECTIONS = [
  { group: 'Indoor TC', firstRow: 12 },
  { group: 'Outdoor TC', firstRow: 43 }
]
const TC_SLOTS = 30

// Where each sheet keeps the report footer
const FOOTER_CELLS = {
  'S (1)': { remarks: 'I67', capacity: 'I71', power: 'M71' },
  'S (2)': { remarks: 'I74', capacity: 'I78', power: 'M78' },
  TC: { remarks: 'I73', capacity: 'I77', power: 'M77' }
}
// Row with the "5 min", "10 min"... headings
const INTERVAL_LABEL_ROW = { 'S (1)': 27, 'S (2)': 10, TC: 10 }

export const loadReportTemplate = async () => {
  const response = await fetch(TEMPLATE_URL)
  if (!response.ok) throw new Error('Could not load the report template.')
  return response.arrayBuffer()
}

export const saveReport = ({ buffer, fileName }) => {
  saveAs(new Blob([buffer], { type: XLSX_TYPE }), fileName)
}

// A report from a streamed test, built from the readings that have arrived -
// all of them once the test has ended, or those so far if the stream stopped
// early. Readings are passed column-wise, as the dashboard holds them:
//   run:    the ingest service's run ({ channels, metadata })
//   times:  Date per reading, oldest first (lab wall clock, carried as UTC)
//   series: { [col_index]: number|null per reading }
//   complete: whether the test ended, rather than the stream stopping
//   snapshot: the reading the Readings sheet shows (default: the last)
// Returns the filled workbook as well as its bytes, so it can be shown in
// the app and downloaded without being built twice.
export const buildStreamedReport = async (templateBuffer, { run, times, series, complete, snapshot }) => {
  const data = readStreamedData({ run, times, series, complete })
  const { workbook, fileName } = await fillReport(templateBuffer, data)
  const at = Number.isInteger(snapshot) ? Math.max(0, Math.min(snapshot, times.length - 1)) : times.length - 1
  addReadingsSheet(workbook, { run, series, index: at, time: times[at], memo: data.memo })
  data.summary.readingsAt = formatClock(times[at])
  return {
    workbook,
    buffer: await workbook.xlsx.writeBuffer(),
    fileName: complete ? fileName : fileName.replace(/\.xlsx$/, ' (partial).xlsx'),
    summary: data.summary
  }
}

const fillReport = async (templateBuffer, data) => {
  const workbook = new ExcelJS.Workbook()
  await workbook.xlsx.load(templateBuffer)

  // The template holds a sheet set for every test type; keep the steady-state ones
  workbook.worksheets
    .filter(sheet => !REPORT_SHEETS.includes(sheet.name))
    .forEach(sheet => workbook.removeWorksheet(sheet.id))

  const s1 = getSheet(workbook, 'S (1)')
  const s2 = getSheet(workbook, 'S (2)')
  const tc = getSheet(workbook, 'TC')

  ;[s1, s2, tc].forEach(sheet => {
    // The template carries a junk first page number (4294963191) that exceljs
    // would otherwise switch on when saving
    sheet.pageSetup.firstPageNumber = undefined
    fillHeader(sheet, data.memo)
    fillIntervalLabels(sheet, INTERVAL_LABEL_ROW[sheet.name], data.samples)
  })

  fillUnitDetails(s1, data.memo)
  fillSampleTimes(s1, data.samples)
  fillMeasuredRows(s1, S1_ROWS, data)
  fillMeasuredRows(s2, S2_ROWS, data)
  fillThermocouples(tc, data)
  s1.getCell('F59').value = data.result || null
  s1.getCell('F63').value = data.memo.Memo || null

  const conclusions = buildConclusions(data)
  ;[s1, s2, tc].forEach(sheet => {
    const cells = FOOTER_CELLS[sheet.name]
    sheet.getCell(cells.remarks).value = text(data.memo.TestName)
    sheet.getCell(cells.capacity).value = conclusions.capacity
    sheet.getCell(cells.power).value = conclusions.power
  })

  const reportName = text(data.memo.ReportNO) || text(data.memo.TestID) || 'performance-test'
  return {
    workbook,
    fileName: `${reportName.replace(/[\\/:*?"<>|]+/g, '-')}.xlsx`
  }
}

// The Readings sheet: every channel at one moment of the test, like the lab
// software's data view. Channels sit under the rig's groups in three blocks of
// Parameter / Value / Unit across an A4 page, in the template's font and fills.
const READINGS_SHEET = 'Readings'
const READINGS_BLOCKS = 3
const READINGS_FIRST_ROW = 5
const THIN = { style: 'thin', color: { argb: 'FF000000' } }
const HAIR = { style: 'hair', color: { argb: 'FF000000' } }
const SHEET_FONT = { name: 'Arial', size: 9 }

const addReadingsSheet = (workbook, { run, series, index, time, memo }) => {
  const sheet = workbook.addWorksheet(READINGS_SHEET, {
    views: [{ showGridLines: false }],
    pageSetup: {
      paperSize: 9,
      orientation: 'portrait',
      fitToPage: true,
      fitToWidth: 1,
      fitToHeight: 0,
      margins: { left: 0.4, right: 0.4, top: 0.5, bottom: 0.5, header: 0.3, footer: 0.3 }
    }
  })
  // A margin column, then per block: name, value, unit and a gap
  const widths = [1.625]
  for (let b = 0; b < READINGS_BLOCKS; b++) widths.push(22, 10, 8, ...(b < READINGS_BLOCKS - 1 ? [2] : []))
  widths.forEach((width, i) => { sheet.getColumn(i + 1).width = width })
  const lastColumn = widths.length

  const title = sheet.getCell(2, 2)
  sheet.mergeCells(2, 2, 2, lastColumn)
  title.value = `Readings at ${formatClock(time)}`
  title.font = { ...SHEET_FONT, size: 14, bold: true }
  title.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFF99' } }
  title.alignment = { horizontal: 'center', vertical: 'middle' }
  title.border = { top: THIN, left: THIN, bottom: THIN, right: THIN }
  sheet.getRow(2).height = 24

  const subtitle = sheet.getCell(3, 2)
  sheet.mergeCells(3, 2, 3, lastColumn)
  subtitle.value = [
    text(memo.TestName),
    text(memo.TestID) && `Test ID ${text(memo.TestID)}`,
    text(memo.ReportNO) && `Report No. ${text(memo.ReportNO)}`,
    formatDateTime(time).slice(0, 10)
  ].filter(Boolean).join('   ·   ')
  subtitle.font = SHEET_FONT
  subtitle.alignment = { horizontal: 'center', vertical: 'middle' }

  const channels = namedChannels(run.channels).map(c => {
    const values = series[String(c.col_index)] || []
    return {
      ...c,
      value: values[index],
      // A channel that never moved off zero is an unwired input
      active: values.some(v => typeof v === 'number' && Math.abs(v) > 0.001)
    }
  })
  const groups = groupChannels(channels)

  // Groups fill the blocks top to bottom, moving to the next block once one
  // holds its share of the rows; a group is never split
  const totalRows = groups.reduce((sum, g) => sum + g.channels.length + 2, -1)
  const share = Math.ceil(totalRows / READINGS_BLOCKS)
  const used = new Array(READINGS_BLOCKS).fill(0)
  let block = 0
  groups.forEach(group => {
    const size = group.channels.length + 1
    if (used[block] > 0 && used[block] + 1 + size > share && block < READINGS_BLOCKS - 1) block++
    if (used[block] > 0) used[block]++
    writeReadingsGroup(sheet, group, READINGS_FIRST_ROW + used[block], 2 + block * 4)
    used[block] += size
  })
}

const writeReadingsGroup = (sheet, group, row, col) => {
  const header = sheet.getCell(row, col)
  sheet.mergeCells(row, col, row, col + 2)
  header.value = group.name
  header.font = { ...SHEET_FONT, bold: true }
  header.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFDAE0F4' } }
  header.alignment = { horizontal: 'left', vertical: 'middle', indent: 1 }
  header.border = { top: THIN, left: THIN, bottom: THIN, right: THIN }

  group.channels.forEach((c, i) => {
    const r = row + 1 + i
    const bottom = i === group.channels.length - 1 ? THIN : HAIR
    const color = c.active ? undefined : { argb: 'FF999999' }
    const name = sheet.getCell(r, col)
    name.value = c.name
    name.font = { ...SHEET_FONT, color }
    name.alignment = { horizontal: 'left', vertical: 'middle' }
    name.border = { left: THIN, bottom }

    const value = sheet.getCell(r, col + 1)
    const v = c.value
    if (typeof v === 'number' && Number.isFinite(v)) {
      const decimals = Number.isInteger(v) ? 0 : readingDecimals(v)
      value.value = round(v, decimals)
      value.numFmt = decimals ? `0.${'0'.repeat(decimals)}` : '0'
    } else {
      value.value = NO_VALUE
    }
    value.font = { ...SHEET_FONT, bold: c.active, color }
    value.alignment = { horizontal: 'right', vertical: 'middle' }
    value.border = { bottom }

    const unit = sheet.getCell(r, col + 2)
    unit.value = c.unit || null
    unit.font = { ...SHEET_FONT, size: 8, color: color || { argb: 'FF555555' } }
    unit.alignment = { horizontal: 'center', vertical: 'middle' }
    unit.border = { right: THIN, bottom }
  })
}

const getSheet = (workbook, name) => {
  const sheet = workbook.getWorksheet(name)
  if (!sheet) throw new Error(`The report template has no "${name}" sheet.`)
  return sheet
}

// The same report data from a streamed test. The lab agent sends the Memo
// table as the run's metadata and the Temp table as its channels; the Report
// table's sampling plan is not sent (the lab software fills it in only when a
// report is printed), so the samples are spaced by `streamedPlan` instead.
const readStreamedData = ({ run, times, series, complete }) => {
  if (!times.length) throw new Error('No readings have arrived for this test yet, so there is nothing to report.')

  // Characters the lab PC's code page could not map arrive as U+FFFD; they
  // carry nothing ("Φ100" becomes "\uFFFD100"), so they are dropped
  const memo = {}
  Object.entries(run.metadata || {}).forEach(([key, value]) => {
    memo[key.trim()] = typeof value === 'string' ? value.replace(/\uFFFD/g, '').trim() : value
  })
  // The report covers the readings received, so its start and end are theirs
  // rather than the Memo's, which the lab software writes at other moments.
  const first = times[0]
  const last = times[times.length - 1]
  memo.TestTime = formatDateTime(first)
  memo.EndTime = formatDateTime(last)

  const channels = (run.channels || []).map(c => ({ ID: c.col_index, Name: c.name || '', GroupName: c.group_name }))
  const plan = streamedPlan(times)
  // Samples are picked on the timestamps alone; only the chosen readings are
  // then assembled into rows keyed like the MDB's (column number -> value).
  const samples = pickSampleRows(times.map((t, index) => ({ DateTime: t, index })), plan)
    .map(s => ({ ...s, row: s.row && streamedRow(s.row, channels, series) }))

  const interval = formatInterval(plan.ReportGroupTime)
  const summary = {
    readings: times.length,
    start: formatClock(first),
    end: formatClock(last),
    spanMs: last - first,
    interval,
    samples: samples.filter(s => s.row).length,
    standardSampling: plan.ReportGroupTime === 5 && plan.ReportGroupNum === SAMPLE_COLUMNS.length,
    complete
  }
  const result = [
    `Built by DTX from ${summary.readings.toLocaleString('en-US')} streamed readings, ${summary.start} to ${summary.end}; samples ${interval} apart.`,
    complete ? '' : 'Readings stopped before the test was completed.'
  ].filter(Boolean).join(' ')

  return { memo, result, channels, ...indexChannels(channels), samples, summary }
}

const streamedRow = ({ DateTime, index }, channels, series) => {
  const row = { DateTime }
  channels.forEach(c => {
    const values = series[String(c.ID)]
    row[String(c.ID)] = values ? values[index] : null
  })
  return row
}

// The lab reports seven readings five minutes apart, ending with the test. A
// stream that stopped before 35 minutes of readings existed would leave most
// columns empty, so shorter runs get the widest round spacing that still fits
// seven readings - or, for very short runs, an even split in whole seconds.
const STEP_MINUTES = [5, 3, 2, 1, 0.5]
const streamedPlan = (times) => {
  const spanMin = (times[times.length - 1] - times[0]) / 60000
  const count = Math.min(SAMPLE_COLUMNS.length, times.length)
  const step = STEP_MINUTES.find(m => m * count <= spanMin) ??
    Math.max(1, Math.floor((spanMin * 60) / count)) / 60
  return { ReportGroupTime: step, ReportGroupNum: count }
}

// Report rows name their MDB channel as the lab software wrote it, including
// the phase marks that survive only as mojibake ("Power ¢ñ #1" is phase I).
// Streamed channel names arrive already cleaned ("Power #1"), so a name that
// does not match exactly is looked up cleaned too; the first channel with
// that name is phase I, as the meters log phases I, II, III, then the total.
// A replayed test names the phases instead ("Power #1 R" ... "Power #1
// Total"), so as a last resort the phase is dropped from the name.
const PHASE_SUFFIX = / (r|s|t|line|avg|total)$/
const indexChannels = (channels) => {
  const channelsByName = {}
  const channelsByCleanName = {}
  const channelsByBaseName = {}
  ;[...channels].sort((a, b) => a.ID - b.ID).forEach(c => {
    const name = c.Name?.trim()
    if (!name) return
    if (!channelsByName[name]) channelsByName[name] = c
    const clean = cleanText(name).toLowerCase()
    if (!channelsByCleanName[clean]) channelsByCleanName[clean] = c
    const base = clean.replace(PHASE_SUFFIX, '')
    if (!channelsByBaseName[base]) channelsByBaseName[base] = c
  })
  return { channelsByName, channelsByCleanName, channelsByBaseName }
}

const findChannel = (data, name) => {
  const clean = cleanText(name).toLowerCase()
  return data.channelsByName[name] || data.channelsByCleanName[clean] || data.channelsByBaseName[clean] || null
}

// The lab software reports one reading every ReportGroupTime minutes after
// ReportStartTime, ReportGroupNum times (the template has room for 7). Each
// sample is the logged row nearest that time; a gap in the log leaves it empty.
// A test nobody printed a report for keeps Access's empty date (1899-12-30) as
// its start; that, like any start outside the log, falls back to the last
// readings of the test.
const pickSampleRows = (rows, plan) => {
  const intervalMin = Number(plan.ReportGroupTime) > 0 ? Number(plan.ReportGroupTime) : 5
  const count = Math.min(Number(plan.ReportGroupNum) > 0 ? Number(plan.ReportGroupNum) : SAMPLE_COLUMNS.length, SAMPLE_COLUMNS.length)
  const firstTime = rows[0].DateTime.getTime()
  const lastTime = rows[rows.length - 1].DateTime.getTime()
  const planned = plan.ReportStartTime instanceof Date ? plan.ReportStartTime.getTime() : NaN
  const startTime = planned >= firstTime - intervalMin * 60000 && planned <= lastTime
    ? planned
    : lastTime - count * intervalMin * 60000
  const maxGapMs = (intervalMin * 60000) / 2

  const samples = []
  let rowIdx = 0
  for (let i = 1; i <= count; i++) {
    const target = startTime + i * intervalMin * 60000
    while (rowIdx < rows.length - 1 &&
      Math.abs(rows[rowIdx + 1].DateTime - target) <= Math.abs(rows[rowIdx].DateTime - target)) {
      rowIdx++
    }
    const row = rows[rowIdx]
    samples.push({
      minutes: i * intervalMin,
      row: Math.abs(row.DateTime - target) <= maxGapMs ? row : null
    })
  }
  return samples
}

const fillHeader = (sheet, memo) => {
  const heating = /heat/i.test(memo.TestStyle || '')
  sheet.getCell('E3').value = text(memo.TestRequestNum)
  sheet.getCell('E4').value = text(memo.TestID)
  sheet.getCell('E5').value = text(memo.ReportNO)
  sheet.getCell('E6').value = text(memo.RequestedBy)
  sheet.getCell('E7').value = text(memo.FormatNO)
  sheet.getCell('E8').value = text(memo.LabName)
  sheet.getCell('M3').value = text(memo.UnitType)
  sheet.getCell('M4').value = text(memo.ProductModel)
  sheet.getCell('M5').value = text(memo.TestName)
  sheet.getCell('M6').value = [text(memo.RefType), text(memo.RefrigerantCharge)].filter(Boolean).join(' - ')
  sheet.getCell('M7').value = ratedWatts(heating ? memo.RatedHeatingCapacity : memo.RatedCoolingCapacity)
  sheet.getCell('M8').value = ratedWatts(heating ? memo.RatedHeatingPower : memo.RatedCoolingPower)
  // "2026-08-18 10:02:08" is shown as "26-08-18 10:02:08"
  sheet.getCell('V3').value = text(memo.TestTime).replace(/^\d{2}(\d{2}-)/, '$1')
  sheet.getCell('V4').value = text(memo.EndTime).replace(/^\d{2}(\d{2}-)/, '$1')
  sheet.getCell('U5').value = text(memo.RatedVoltage)
  sheet.getCell('U6').value = text(memo.RatedCurrent)
  sheet.getCell('U7').value = text(memo.RatedFrequency)
  sheet.getCell('U8').value = text(memo.ConnectingPipe)
}

const fillUnitDetails = (sheet, memo) => {
  const cells = {
    E10: memo.IndoorModelNO,
    E11: memo.IndoorSerialNO,
    E12: memo.IndoorNosofUnit,
    E13: memo.IndoorFan,
    G14: memo.IndoorModel,
    G15: memo.IndoorSpeed,
    G16: memo.IndoorCoilsize,
    G17: memo.IndoorRow,
    G18: memo.IndoorTubeDia,
    G19: memo.IndoorNosCircuit,
    P10: memo.OutdoorModelNO,
    P11: memo.OutdoorSerialNO,
    P12: memo.OutdoorNosofUnit,
    P13: memo.OutdoorFan,
    R14: memo.OutdoorModel,
    R15: memo.OutdoorSpeed,
    R16: memo.OutdoorCoilsize,
    R17: memo.OutdoorRow,
    R18: memo.OutdoorTubeDia,
    R19: memo.OutdoorNosCircuit,
    E20: memo.CompressorModel,
    R20: memo.AirANozzles,
    E21: memo.TestStandard,
    R21: memo.ExpDevice,
    R22: memo.FanSpeed,
    E23: memo.TestPurpose
  }
  Object.entries(cells).forEach(([cell, value]) => {
    sheet.getCell(cell).value = text(value)
  })
  const numericCells = {
    W23: memo.IndoorStaticPressure,
    N24: memo.IndoorEnteringDB,
    N25: memo.IndoorEnteringWB,
    W24: memo.OutdoorEnteringDB,
    W25: memo.OutdoorEnteringWB
  }
  Object.entries(numericCells).forEach(([cell, value]) => {
    sheet.getCell(cell).value = numberOrText(value)
  })
}

const fillIntervalLabels = (sheet, row, samples) => {
  SAMPLE_COLUMNS.forEach((col, i) => {
    sheet.getCell(`${col}${row}`).value = samples[i] ? formatInterval(samples[i].minutes) : null
  })
}

const fillSampleTimes = (sheet, samples) => {
  const times = samples.map(s => (s.row ? formatClock(s.row.DateTime) : NO_VALUE))
  SAMPLE_COLUMNS.forEach((col, i) => {
    sheet.getCell(`${col}28`).value = i < times.length ? times[i] : null
  })
  sheet.getCell(`${AVERAGE_COLUMN}28`).value = times.length ? times[times.length - 1] : null
}

const fillMeasuredRows = (sheet, rowDefs, data) => {
  rowDefs.forEach(def => {
    const channel = def.channel ? findChannel(data, def.channel) : null
    const values = data.samples.map(s => (channel && s.row ? readValue(s.row, channel, def) : null))
    writeRow(sheet, def.row, values, def.decimals)
  })
}

const fillThermocouples = (sheet, data) => {
  TC_SECTIONS.forEach(({ group, firstRow }) => {
    const groupChannels = data.channels
      .filter(c => c.GroupName === group)
      .sort((a, b) => a.ID - b.ID)
      .slice(0, TC_SLOTS)
    for (let slot = 0; slot < TC_SLOTS; slot++) {
      const row = firstRow + slot
      const channel = groupChannels[slot]
      const label = `${String(slot + 1).padStart(2, '0')}: ${channel ? channel.Name.trim() : ''}`
      sheet.getCell(`B${row}`).value = label
      const values = data.samples.map(s => (channel && s.row ? readValue(s.row, channel, { decimals: 1 }) : null))
      // A thermocouple reading exactly 0 throughout isn't connected
      const connected = values.some(v => v !== null && v !== 0)
      writeRow(sheet, row, connected ? values : values.map(() => null), 1)
    }
  })
}

const readValue = (row, channel, def) => {
  const raw = row[String(channel.ID)]
  if (typeof raw !== 'number' || !Number.isFinite(raw)) return null
  return round(raw * (def.factor || 1), def.decimals)
}

// Writes the sample values and their average; unused sample columns are cleared
const writeRow = (sheet, row, values, decimals) => {
  SAMPLE_COLUMNS.forEach((col, i) => {
    sheet.getCell(`${col}${row}`).value = i < values.length ? (values[i] ?? NO_VALUE) : null
  })
  const numbers = values.filter(v => typeof v === 'number')
  sheet.getCell(`${AVERAGE_COLUMN}${row}`).value = numbers.length
    ? round(numbers.reduce((sum, v) => sum + v, 0) / numbers.length, decimals)
    : NO_VALUE
}

// "Capacity: 6,513  [Rated:0(0%)]" and "Power: 2,021.2  [Rated:2,110(95.79%)]",
// from the last sample, as the lab software prints them
const buildConclusions = (data) => {
  const heating = /heat/i.test(data.memo.TestStyle || '')
  const lastRow = [...data.samples].reverse().find(s => s.row)?.row
  const lastValue = (name) => {
    const channel = findChannel(data, name)
    return lastRow && channel ? readValue(lastRow, channel, { decimals: 1 }) : null
  }
  const describe = (label, value, rated, decimals) => {
    const shown = value === null ? NO_VALUE : formatNumber(value, decimals)
    const ratedNumber = Number(String(rated ?? '').replace(/,/g, ''))
    const ratedPart = text(rated) && Number.isFinite(ratedNumber)
      ? `${formatNumber(ratedNumber, 0)}(${ratedNumber && value !== null ? round((value / ratedNumber) * 100, 2) : 0}%)`
      : NO_VALUE
    return `${label}: ${shown}  [Rated:${ratedPart}]`
  }
  return {
    capacity: describe('Capacity', lastValue('UUT Capacity'),
      heating ? data.memo.RatedHeatingCapacity : data.memo.RatedCoolingCapacity, 0),
    power: describe('Power', lastValue('UUT Power'),
      heating ? data.memo.RatedHeatingPower : data.memo.RatedCoolingPower, 1)
  }
}

const text = (value) => (value === null || value === undefined ? '' : String(value).trim())

const numberOrText = (value) => {
  const number = Number(text(value))
  return text(value) && Number.isFinite(number) ? number : text(value)
}

// "2110" -> "2,110 W"; "--" and blanks are kept as they are
const ratedWatts = (value) => {
  const number = Number(text(value).replace(/,/g, ''))
  return text(value) && Number.isFinite(number) ? `${formatNumber(number, 0)} W` : text(value)
}

const round = (value, decimals) => Number(value.toFixed(decimals))

const formatNumber = (value, decimals) =>
  value.toLocaleString('en-US', { maximumFractionDigits: decimals })

// MDB times are wall-clock times stored without a time zone, which mdb-reader
// returns as UTC - so read them back with the UTC getters
const formatClock = (date) =>
  [date.getUTCHours(), date.getUTCMinutes(), date.getUTCSeconds()].map(n => String(n).padStart(2, '0')).join(':')

// "2026-08-19 07:01:19", the way the Memo table writes times
const formatDateTime = (date) =>
  [date.getUTCFullYear(), date.getUTCMonth() + 1, date.getUTCDate()].map(n => String(n).padStart(2, '0')).join('-') +
  ' ' + formatClock(date)

// Column headings: "5 min" for the lab's whole-minute spacing; a short
// streamed run's finer spacing reads "30 s" or "1.5 min"
const formatInterval = (minutes) => {
  const seconds = Math.round(minutes * 60)
  if (seconds % 60 === 0) return `${seconds / 60} min`
  return seconds < 120 ? `${seconds} s` : `${round(seconds / 60, 1)} min`
}
